import { useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useWarehouses,
  useCreateWarehouse,
  useUpdateWarehouse,
  useToggleWarehouse,
  useDeleteWarehouse,
  isWarehouseActive,
  type Warehouse,
  type WarehouseInput,
} from './useInventory';
import { formatDate } from '../../shared/utils/format';

export function WarehousesPage(): JSX.Element {
  const { addToast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editWarehouse, setEditWarehouse] = useState<Warehouse | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formData, setFormData] = useState<WarehouseInput>({ name: '', code: '' });

  const { data: warehouses, isLoading, error } = useWarehouses();
  const createWarehouse = useCreateWarehouse();
  const updateWarehouse = useUpdateWarehouse();
  const toggleWarehouse = useToggleWarehouse();
  const deleteWarehouse = useDeleteWarehouse();

  const openCreateModal = () => {
    setEditWarehouse(null);
    setFormData({ name: '', code: '' });
    setModalOpen(true);
  };

  const openEditModal = (w: Warehouse) => {
    setEditWarehouse(w);
    setFormData({ name: w.name, code: w.code });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    try {
      if (editWarehouse) {
        await updateWarehouse.mutateAsync({ id: editWarehouse.id, ...formData });
        addToast('Armazém atualizado com sucesso', 'success');
      } else {
        await createWarehouse.mutateAsync(formData);
        addToast('Armazém criado com sucesso', 'success');
      }
      setModalOpen(false);
    } catch {
      addToast('Erro ao salvar armazém', 'error');
    }
  };

  const handleToggle = async (w: Warehouse) => {
    try {
      const ativo = isWarehouseActive(w);
      await toggleWarehouse.mutateAsync({ id: w.id, active: !ativo });
      addToast(ativo ? 'Armazém desativado' : 'Armazém ativado', 'success');
    } catch {
      addToast('Erro ao alterar status', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWarehouse.mutateAsync(id);
      addToast('Armazém excluído com sucesso', 'success');
      setDeleteConfirmId(null);
    } catch {
      addToast('Erro ao excluir armazém', 'error');
    }
  };

  const isSaving = createWarehouse.isPending || updateWarehouse.isPending;

  /* ── Loading State ── */
  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Armazéns</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Armazéns e locais de estocagem.</p>
          </div>
        </div>
        <Card>
          <CardBody>
            <LoadingSkeleton count={6} height="48px" />
          </CardBody>
        </Card>
      </div>
    );
  }

  /* ── Error State ── */
  if (error) {
    return (
      <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Armazéns</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Armazéns e locais de estocagem.</p>
          </div>
        </div>
        <Card>
          <CardBody>
            <div className="state-error" role="alert">
              <p>Erro ao carregar armazéns</p>
              <p className="state-detail">{(error as Error).message}</p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Armazéns</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Armazéns e locais de estocagem.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button onClick={openCreateModal}>+ Novo Armazém</Button>
        </div>
      </div>

      {/* ── Empty State ── */}
      {(!warehouses || warehouses.length === 0) ? (
        <Card>
          <CardBody>
            <div className="state-empty">
              <p>Nenhum armazém cadastrado</p>
              <p className="state-detail">Crie seu primeiro armazém para começar.</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        /* ── Data State ── */
        <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm overflow-hidden">
          <div className="p-md border-b border-outline-variant flex items-center justify-between gap-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Armazéns Cadastrados</h3>
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {warehouses.length} registro{warehouses.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="wh-table">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {warehouses.map((w) => (
                  <tr key={w.id}>
                    <td><code>{w.code}</code></td>
                    <td>{w.name}</td>
                    <td>
                      <Badge variant={isWarehouseActive(w) ? 'success' : 'neutral'} dot>
                        {isWarehouseActive(w) ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatDate(w.createdAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="wh-actions">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(w)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant={isWarehouseActive(w) ? 'secondary' : 'primary'}
                          onClick={() => handleToggle(w)}
                        >
                          {isWarehouseActive(w) ? 'Desativar' : 'Ativar'}
                        </Button>
                        {deleteConfirmId === w.id ? (
                          <div className="wh-confirm">
                            <span>Excluir?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDelete(w.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(w.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Create/Edit Modal ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editWarehouse ? 'Editar Armazém' : 'Novo Armazém'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              Salvar
            </Button>
          </>
        }
      >
        <div className="wh-form">
          <Input
            label="Código"
            placeholder="Ex: SP01"
            value={formData.code}
            onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
          />
          <Input
            label="Nome"
            placeholder="Ex: Galpão São Paulo"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          />
        </div>
      </Modal>

      <style>{`
        .wh-table { overflow-x: auto; }
        .wh-table table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .wh-table th { padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; position: sticky; top: 0; z-index: 1;}
        .wh-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .wh-table tbody tr:hover td { background: #eceef0; }
        .wh-table code { background: #f2f4f6; padding: 0.125rem 0.375rem; border-radius: 4px; font-size: 0.8125rem; }
        .wh-actions { display: inline-flex; align-items: center; gap: 0.5rem; }
        .wh-confirm { display: inline-flex; align-items: center; gap: 0.25rem; }
        .wh-confirm span { font-size: 0.75rem; color: #ba1a1a; font-weight: 600; }
        .wh-form { display: flex; flex-direction: column; gap: 1rem; }
        .state-error { text-align: center; padding: 2rem; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 2rem; color: #74777f; }
        .state-empty .state-detail { font-size: 0.875rem; margin-top: 0.5rem; }
      `}</style>
    </div>
  );
}
